import type {Component, Editor} from 'grapesjs';
import type {EmailMakerImmutableStore} from '@EmailMakerReact/email-maker-react.types';
import type {EmailMakerControl} from '@EmailMakerReact/layout/email-maker-right-panel/right-panel-registry.types';

/** Context handed to an action's `onActivate`; mirrors what the action strip already holds. */
export interface EmailMakerActionActivateCtx {
  editor: Editor;
  target: Component;
  store: EmailMakerImmutableStore;
}

export enum TileComplexity {
  SIMPLE = 'simple',
  COMPLEX = 'complex',
}

export interface EmailMakerTileMeta {
  icon: string;
  labelKey: string;
  complexity: TileComplexity;
}

/** One resolved control tile entry with a stable `flatIndex` for the mobile sheet's re-resolution. */
export interface EmailMakerControlTileEntry {
  control: EmailMakerControl;
  meta: EmailMakerTileMeta;
  /** Resolved target for the control — computed via resolveTarget ?? selected. */
  target: Component;
  /** Stable index into the flattened, visibility-filtered property list. */
  flatIndex: number;
}

export interface BlockCategoryTileEntry {
  id: string;
  icon: string;
  labelKey: string;
}

/** Opt-in metadata that flags a non-icon-button control as a mobile action-strip button that opens the shared control sheet. */
export interface EmailMakerActionMeta {
  icon: string;
  labelKey: string;
  complexity: TileComplexity;
  /** When set, the action strip runs this handler directly instead of opening the mobile control sheet (used by promoted media actions). */
  onActivate?: (ctx: EmailMakerActionActivateCtx) => void;
}

/** One resolved action-strip entry. Inline when `actionMeta` is absent (icon-button); sheet-opening when `actionMeta` is present. */
export interface EmailMakerResolvedAction {
  /** The icon-button control rendered inline; absent for sheet-opening and promoted actions, which render via `actionMeta`. */
  control?: EmailMakerControl;
  /** Resolved target for the control — computed via resolveTarget ?? selected. */
  target: Component;
  /** Stable React key: the action's position in the flattened, visibility-filtered property list. */
  key: string;
  /** Stable index into the flattened, visibility-filtered property list — shared with `getResolvedTiles`. */
  flatIndex: number;
  /** Present only for sheet-opening actions; absent for inline instant/toggle icon-button actions. */
  actionMeta?: EmailMakerActionMeta;
}

/** A control resolved at a given `flatIndex`; `labelKey` titles the sheet header. */
export interface EmailMakerResolvedControlAtIndex {
  control: EmailMakerControl;
  /** Resolved target for the control — computed via resolveTarget ?? selected. */
  target: Component;
  labelKey: string;
}
